import {
  ActiveChatNotesSession,
  createChatNotesCompatibilityAdapter,
  ChatNotesCompatibilityAdapter
} from './adapter';
import {ChatNotesClient} from './client';
import {readChatNotesIntegrationConfig, ChatNotesIntegrationConfig} from './config';
import {
  ChatNotesCoreStatus,
  DEVELOPMENT_AUTH_CAPABILITY,
  TELEGRAM_AUTH_CAPABILITY
} from './contract';
import {ChatNotesError} from './errors';
import {ChatNotesWebKApiBridge} from './webk-api-bridge';

export type ChatNotesIntegrationSnapshot =
  {phase: 'disabled'} |
  {phase: 'idle'} |
  {phase: 'connecting'} |
  {phase: 'ready'; status: ChatNotesCoreStatus} |
  {phase: 'authenticating'; status: ChatNotesCoreStatus} |
  {phase: 'authenticated'; status: ChatNotesCoreStatus; session: ActiveChatNotesSession} |
  {phase: 'failed'; error: ChatNotesError};

type AdapterFactory = (config: Extract<ChatNotesIntegrationConfig, {enabled: true}>) =>
  ChatNotesCompatibilityAdapter;

function defaultAdapterFactory(
  config: Extract<ChatNotesIntegrationConfig, {enabled: true}>
): ChatNotesCompatibilityAdapter {
  return createChatNotesCompatibilityAdapter(new ChatNotesClient({
    baseUrl: config.baseUrl,
    timeoutMs: config.timeoutMs
  }));
}

export class ChatNotesIntegrationRuntime {
  private readonly adapter?: ChatNotesCompatibilityAdapter;
  private readonly webkBridge?: ChatNotesWebKApiBridge;
  private readonly webkBridgeEnabled: boolean;
  private authentication?: Promise<ChatNotesIntegrationSnapshot>;
  private bridgeActivation?: Promise<boolean>;
  private webkBridgeActive = false;
  private connection?: Promise<ChatNotesIntegrationSnapshot>;
  private snapshot: ChatNotesIntegrationSnapshot;

  constructor(
    config: ChatNotesIntegrationConfig,
    adapterFactory: AdapterFactory = defaultAdapterFactory
  ) {
    if(!config.enabled) {
      this.webkBridgeEnabled = false;
      this.snapshot = {phase: 'disabled'};
      return;
    }

    this.webkBridgeEnabled = config.webkBridgeEnabled;
    this.snapshot = {phase: 'idle'};
    try {
      this.adapter = adapterFactory(config);
      this.webkBridge = new ChatNotesWebKApiBridge(this.adapter);
    } catch(error) {
      const integrationError = error instanceof ChatNotesError ? error : new ChatNotesError(
        'INVALID_CONFIGURATION',
        'Chat Notes integration configuration is invalid.',
        {cause: error}
      );
      this.snapshot = {phase: 'failed', error: integrationError};
    }
  }

  public getSnapshot(): ChatNotesIntegrationSnapshot {
    return this.snapshot;
  }

  public isWebKBridgeActive(): boolean {
    return this.webkBridgeActive;
  }

  public shouldUseWebKBridge(): boolean {
    return this.webkBridgeEnabled && this.snapshot.phase === 'authenticated';
  }

  public activateWebKBridge(): Promise<boolean> {
    if(this.webkBridgeActive) return Promise.resolve(true);
    if(this.bridgeActivation) return this.bridgeActivation;
    if(!this.shouldUseWebKBridge() || !this.webkBridge || this.snapshot.phase !== 'authenticated') {
      return Promise.resolve(false);
    }

    const user = this.snapshot.session.user;
    this.bridgeActivation = import('./webk-bridge-transport')
    .then(({activateChatNotesWebKBridge}) => activateChatNotesWebKBridge(this.webkBridge!, user))
    .then(() => this.webkBridgeActive = true)
    .catch((error) => {
      this.bridgeActivation = undefined;
      throw error;
    });
    return this.bridgeActivation;
  }

  public connect(): Promise<ChatNotesIntegrationSnapshot> {
    if(!this.adapter) return Promise.resolve(this.snapshot);
    if(this.snapshot.phase === 'authenticated') return Promise.resolve(this.snapshot);
    if(this.connection) return this.connection;

    this.snapshot = {phase: 'connecting'};
    this.connection = this.adapter.connect()
    .then((status): ChatNotesIntegrationSnapshot => this.snapshot = {phase: 'ready', status})
    .catch((error: unknown): ChatNotesIntegrationSnapshot => {
      const integrationError = error instanceof ChatNotesError ? error : new ChatNotesError(
        'NETWORK_ERROR',
        'Chat Notes integration failed.',
        {cause: error}
      );
      return this.snapshot = {phase: 'failed', error: integrationError};
    })
    .then((snapshot) => {
      if(snapshot.phase === 'failed') this.connection = undefined;
      return snapshot;
    });

    return this.connection;
  }

  public async start(initData?: string): Promise<ChatNotesIntegrationSnapshot> {
    const connected = await this.connect();
    if(connected.phase !== 'ready') return connected;
    if(initData) return this.authenticateTelegram(initData, connected.status);
    if(connected.status.integration.capabilities.includes(DEVELOPMENT_AUTH_CAPABILITY)) {
      return this.authenticateDevelopment(connected.status);
    }
    return connected;
  }

  private authenticateDevelopment(status: ChatNotesCoreStatus): Promise<ChatNotesIntegrationSnapshot> {
    if(!this.adapter) return Promise.resolve(this.snapshot);
    if(this.authentication) return this.authentication;
    this.snapshot = {phase: 'authenticating', status};
    this.authentication = this.adapter.authenticateDevelopment()
    .then((session): ChatNotesIntegrationSnapshot =>
      this.snapshot = {phase: 'authenticated', status, session})
    .catch((error: unknown): ChatNotesIntegrationSnapshot => {
      const integrationError = error instanceof ChatNotesError ? error : new ChatNotesError(
        'NETWORK_ERROR',
        'Chat Notes local authentication failed.',
        {cause: error}
      );
      return this.snapshot = {phase: 'failed', error: integrationError};
    })
    .then((snapshot) => {
      if(snapshot.phase === 'failed') this.authentication = undefined;
      return snapshot;
    });
    return this.authentication;
  }

  private authenticateTelegram(
    initData: string,
    status: ChatNotesCoreStatus
  ): Promise<ChatNotesIntegrationSnapshot> {
    if(!this.adapter) return Promise.resolve(this.snapshot);
    if(this.authentication) return this.authentication;
    if(!status.integration.capabilities.includes(TELEGRAM_AUTH_CAPABILITY)) {
      this.snapshot = {phase: 'failed', error: new ChatNotesError(
        'INCOMPATIBLE_PROTOCOL',
        `Chat Notes Core does not provide ${TELEGRAM_AUTH_CAPABILITY}.`
      )};
      return Promise.resolve(this.snapshot);
    }

    this.snapshot = {phase: 'authenticating', status};
    this.authentication = this.adapter.authenticateTelegram(initData)
    .then((session): ChatNotesIntegrationSnapshot =>
      this.snapshot = {phase: 'authenticated', status, session})
    .catch((error: unknown): ChatNotesIntegrationSnapshot => {
      const integrationError = error instanceof ChatNotesError ? error : new ChatNotesError(
        'NETWORK_ERROR',
        'Chat Notes authentication failed.',
        {cause: error}
      );
      return this.snapshot = {phase: 'failed', error: integrationError};
    })
    .then((snapshot) => {
      if(snapshot.phase === 'failed') this.authentication = undefined;
      return snapshot;
    });
    return this.authentication;
  }
}

const chatNotesIntegration = new ChatNotesIntegrationRuntime(
  readChatNotesIntegrationConfig(import.meta.env)
);

export default chatNotesIntegration;
